import React, { useEffect, useRef, useState, useCallback } from "react";
import { Select } from "antd";
import { serverFetch } from "@extensions";
import "./catalog-sale-clients.css";

const SELECT_KEEP_OPEN_MS = 500;

/**
 * Выбор покупателей для услуги: сам клиент и члены его группы.
 * Если группы нет (только сам клиент) - компонент не отображается.
 * refreshKey - внешний запрос обновления: его увеличивает родитель
 * после сохранения данных участника группы.
 * bookedClientIds - участники, уже занявшие место в занятии:
 * их можно только посмотреть, повторно записать нельзя.
 */
const CatalogSaleClients = ({ selectedIds, onChange, onClientsLoaded, refreshKey, bookedClientIds = [], onGroupVisibilityChange }) => {
  // null - данные ещё не загружены, [] - группы нет
  const [familyMembers, setFamilyMembers] = useState(null);
  const [selfClient, setSelfClient] = useState(null);
  const [loading, setLoading] = useState(true);
  // Список покупателей собираем сразу из двух ответов и храним в состоянии:
  // ссылка на массив стабильна между рендерами, поэтому родитель получает
  // его только при реальном изменении состава
  const [clients, setClients] = useState([]);
  // Раскрыт ли список добавления участника
  const [selectOpen, setSelectOpen] = useState(false);
  // Курсор над раскрытым списком: по нему отличаем клик по участнику от клика мимо
  const [dropdownHovered, setDropdownHovered] = useState(false);
  // Время последнего выбора: на телефоне наведения нет, закрытие
  // списка сразу после выбора не должно происходить
  const lastSelectAtRef = useRef(0);

  const fetchData = useCallback(() => {
    setLoading(true);
    serverFetch("OnlineClient/info", { method: "GET" })
      .then((selfData) => {
        setSelfClient(selfData);
        // Семью запрашиваем вслед за профилем: так список собирается
        // один раз из полных данных, без промежуточных состояний
        return serverFetch("OnlineClient/FamilyList", { method: "GET" })
          .then((familyData) => {
            const family = Array.isArray(familyData) ? familyData : [];
            setFamilyMembers(family);
            setClients([selfData, ...family]);
          })
          .catch((e) => {
            console.error("Ошибка получения списка группы:", e);
            setFamilyMembers([]);
            setClients([selfData]);
          });
      })
      .catch((e) => {
        // Профиль не пришёл: клиент не авторизован. Блок выбора не показываем,
        // состав группы неизвестен - считаем, что выбирать некого
        console.error("Ошибка получения профиля:", e);
        setFamilyMembers([]);
        setClients([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Перечитываем покупателей по запросу извне: иначе после правки участника группы
  // в списке останутся прежние данные до перезагрузки страницы
  useEffect(() => {
    if (refreshKey > 0) fetchData();
  }, [refreshKey, fetchData]);

  // Ждём оба ответа, чтобы не мигало частично заполненным списком

  // Сообщаем наружу список покупателей: он нужен для проверки обязательных полей участников группы.
  // clients лежит в состоянии, поэтому эффект срабатывает только при
  // реальном изменении состава и не затирает свежие данные
  useEffect(() => {
    if (onClientsLoaded && clients.length > 1) {
      onClientsLoaded(clients);
    }
  }, [clients, onClientsLoaded]);

  const toggleClient = (clientId) => {
    const isSelected = selectedIds.includes(clientId);
    // Выбор снимается свободно: кнопка "Купить" заблокируется, если не выбрал никто
    const newIds = isSelected
      ? selectedIds.filter((id) => id !== clientId)
      : [...selectedIds, clientId];
    onChange(newIds);
  };

  // Добавление из списка: после выбора список остаётся раскрытым
  const handleSelectAdd = (clientId) => {
    if (!clientId) return;
    toggleClient(clientId);
    // Фиксируем момент выбора: на телефоне наведения нет,
    // по этому времени понимаем, что закрытие вызвано выбором
    lastSelectAtRef.current = Date.now();
    setSelectOpen(true);
  };

  const handleDropdownVisibleChange = (open) => {
    if (!open && dropdownHovered) return;
    if (!open && Date.now() - lastSelectAtRef.current < SELECT_KEEP_OPEN_MS) return;
    setSelectOpen(open);
  };

  // Блок выбора покупателей показывается только при наличии группы.
  // Без группы покупка оформляется на самого клиента, и родителю
  // не нужно требовать выбор покупателя.
  // Эффект до ранних возвратов: порядок вызова хуков должен быть постоянным
  useEffect(() => {
    if (onGroupVisibilityChange) {
      onGroupVisibilityChange(clients.length > 1);
    }
  }, [clients, onGroupVisibilityChange]);

  // Без группы покупатель один - сам клиент. Выбираем его явно, чтобы остальное
  // поведение совпадало с покупкой на одного участника группы: счётчики,
  // проверка занятых мест и цена считаются тем же кодом
  useEffect(() => {
    if (selfClient?.id && familyMembers !== null && familyMembers.length === 0 && !selectedIds.includes(selfClient.id)) {
      onChange([selfClient.id]);
    }
  }, [selfClient, familyMembers, selectedIds, onChange]);

  if (loading) {
    return <div className="catalog-sale-clients-loading">Загрузка...</div>;
  }

  // Группы нет - выбирать некого
  if (clients.length <= 1) {
    return null;
  }

  const selectedClients = selectedIds
    .map((id) => clients.find((client) => client.id === id))
    .filter(Boolean);

  // Уже записанных в занятие показываем только в списке: вызов продажи их
  // не касается. В списке они уходят в конец и выбраться не могут
  const sortedClients = [...clients].sort((clientA, clientB) => {
    const bookedA = bookedClientIds.includes(clientA.id);
    const bookedB = bookedClientIds.includes(clientB.id);
    if (bookedA === bookedB) return 0;
    return bookedA ? 1 : -1;
  });

  return (
    <div className="catalog-sale-clients">
      <div className="catalog-sale-clients-title">Для кого покупаем</div>

      {/* Выбранные участники - удаляемыми пилюлями */}
      <div className="catalog-sale-clients-pills">
        {selectedClients.length === 0 && (
          <span className="catalog-sale-clients-pills-empty">Никто не выбран</span>
        )}
        {selectedClients.map((client) => (
          <span className="catalog-sale-clients-pill" key={client.id}>
            <span className="catalog-sale-clients-pill-name" title={client.name}>{client.name}</span>
            {client.id === selfClient?.id && <span className="catalog-sale-clients-pill-self">Я</span>}
            <button
              type="button"
              className="catalog-sale-clients-pill-remove"
              aria-label={`Убрать ${client.name}`}
              onClick={() => toggleClient(client.id)}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      {/* Селектор добавления: участники с отметкой о выборе.
          Уже записанные - в конце списка и без возможности выбора */}
      <Select
        className="catalog-sale-clients-select"
        value={null}
        placeholder="Добавить участника"
        onChange={handleSelectAdd}
        open={selectOpen}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        options={sortedClients.map((client) => {
          const booked = bookedClientIds.includes(client.id);
          return {
            value: client.id,
            label: booked ? `${client.name} (уже записан)` : client.name,
            selected: selectedIds.includes(client.id),
            isSelf: client.id === selfClient?.id,
            // Повторно записать уже занятого участника нельзя
            disabled: booked,
          };
        })}
        optionRender={(option) => (
          <div className={`catalog-sale-clients-option ${option.data.selected ? "catalog-sale-clients-option_selected" : ""} ${option.data.disabled ? "catalog-sale-clients-option_booked" : ""}`}>
            <span className="catalog-sale-clients-option-name">{option.label}</span>
            {option.data.isSelf && <span className="catalog-sale-clients-option-self">Я</span>}
            {option.data.selected && (
              <span className="catalog-sale-clients-option-mark" aria-hidden>✓</span>
            )}
          </div>
        )}
        dropdownRender={(menu) => (
          <div
            onMouseEnter={() => setDropdownHovered(true)}
            onMouseLeave={() => setDropdownHovered(false)}
          >
            {menu}
          </div>
        )}
      />
    </div>
  );
};

export default CatalogSaleClients;
